import {
    Add,
    AlertModal,
    Box,
    Button,
    DeleteModal,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    formatTimestamp,
    IconButton,
    Info,
    Kebab,
    Modal,
    showToast,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { Trans, useTranslation } from "react-i18next";

import style from "./ai-menu.scss";
import { AIPanelLayout } from "components/ai-panel/AIPanel";
import { FeatureBadge } from "components/feature-badge/FeatureBadge";
import { NavigationLabel } from "components/navigation/navigation-panel/sub-navigation-panel/ui-components/navigation-label/NavigationLabel";
import { Breakpoint } from "domain/breakpoints";
import { aiAgentService, Session } from "services/ai-agent/AiAgentService";
import { isTextTruncated } from "utils/commonFunctions";

import testIds from "testIds.json";

const DELETE_CONFIRMATION_WORD = "DELETE";
const SESSION_NAME_MAX_LENGTH = 200;

const sessionLabel = (session: Session) => session.name ?? formatTimestamp(session.createdAt);

interface ConversationManagementProps {
    sessions: Session[];
    disabled: boolean;
    activeSessionId?: string;
    isMaximized?: boolean;
    layout?: AIPanelLayout;
    onNewConversation: () => void;
    onLoadConversation: (sessionId: string) => void;
    onSessionDeleted: (sessionId: string) => void;
    onSessionRenamed: (sessionId: string, name: string) => void;
    subNavigation?: boolean;
}

interface SessionKebabMenuProps {
    session: Session;
    disabled: boolean;
    onSessionDeleted: (sessionId: string) => void;
    onSessionRenamed: (sessionId: string, name: string) => void;
    containerRef: React.RefObject<HTMLElement>;
    isSubNavigation?: boolean;
}

const SessionKebabMenu = (props: SessionKebabMenuProps) => {
    const { t } = useTranslation();
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isRenameDialogOpen, setIsRenameDialogOpen] = React.useState(false);
    const [newName, setNewName] = React.useState(sessionLabel(props.session));
    const [isRenaming, setIsRenaming] = React.useState(false);
    const [renameFailed, setRenameFailed] = React.useState(false);
    const [deleteFailed, setDeleteFailed] = React.useState(false);

    const handleRenameConfirm = () => {
        const trimmedName = newName.trim();
        setIsRenaming(true);
        aiAgentService
            .renameSession(props.session.sessionId, trimmedName)
            .then(() => {
                setIsRenameDialogOpen(false);
                props.onSessionRenamed(props.session.sessionId, trimmedName);
                showToast(t("AiAgent.renameConversation.successMessage"), "success");
            })
            .catch(() => {
                setIsRenameDialogOpen(false);
                setRenameFailed(true);
            })
            .finally(() => setIsRenaming(false));
    };

    const handleDeleteConfirm = () => {
        setIsDeleting(true);
        aiAgentService
            .deleteSession(props.session.sessionId)
            .then(() => {
                setIsDeleteDialogOpen(false);
                props.onSessionDeleted(props.session.sessionId);
                showToast(t("AiAgent.deleteConversation.successMessage"), "success");
            })
            .catch(() => {
                setIsDeleteDialogOpen(false);
                setDeleteFailed(true);
            })
            .finally(() => setIsDeleting(false));
    };

    const dropdownMenuPosition =
        props.isSubNavigation && window.innerWidth >= Breakpoint.MD ? "bottom-start" : "bottom-end";
    return (
        <>
            <DropdownMenu
                testId={testIds.workArea.aiPanel.historyPanel.kebabMenu.itself}
                position={dropdownMenuPosition}
                dropdownOffset="Xxs"
                closeOnSelection
                flipOptions={{
                    fallbackPlacements: ["top-start"],
                }}
            >
                <DropdownTrigger disabled={props.disabled}>
                    <IconButton size="Sm" disabled={props.disabled} className={style.kebabMenuIcon}>
                        <Kebab color={"--app-aiPanel-iconColor"} />
                    </IconButton>
                </DropdownTrigger>
                <DropdownContent
                    containerRef={props.isSubNavigation ? undefined : props.containerRef}
                    className={style.dropdownContent}
                >
                    <DropdownMenuItem
                        testId={testIds.workArea.aiPanel.historyPanel.kebabMenu.renameButton}
                        onClick={() => {
                            setNewName(sessionLabel(props.session));
                            setIsRenameDialogOpen(true);
                        }}
                    >
                        {t("Common.rename")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        errorStyle
                        disabled={isDeleting}
                        testId={testIds.workArea.aiPanel.historyPanel.kebabMenu.deleteButton}
                        onClick={() => setIsDeleteDialogOpen(true)}
                    >
                        {t("Common.delete")}
                    </DropdownMenuItem>
                </DropdownContent>
            </DropdownMenu>
            <DeleteModal
                title={t("AiAgent.deleteConversation.title")}
                isOpen={isDeleteDialogOpen}
                hideModal={() => setIsDeleteDialogOpen(false)}
                onDelete={handleDeleteConfirm}
                textBundle={{
                    confirmationText: DELETE_CONFIRMATION_WORD,
                    deleteButtonText: t("Common.delete"),
                    cancelButtonText: t("Common.cancel"),
                }}
                dataType={"DELETE_AI_CONVERSATION"}
            >
                <Trans i18nKey="AiAgent.deleteConversation.confirmationMessage" components={{ p: <p /> }} />
            </DeleteModal>
            <Modal
                isOpen={isRenameDialogOpen}
                hideModal={() => setIsRenameDialogOpen(false)}
                title={t("AiAgent.renameConversation.title")}
                buttons={
                    <>
                        <Button
                            variant="Secondary"
                            onClick={() => setIsRenameDialogOpen(false)}
                            disabled={isRenaming}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("Common.cancel")}
                        </Button>
                        <Button
                            variant="Primary"
                            isLoading={isRenaming}
                            disabled={
                                isRenaming ||
                                newName.trim().length === 0 ||
                                newName.trim().length > SESSION_NAME_MAX_LENGTH
                            }
                            onClick={handleRenameConfirm}
                            testId={testIds.workArea.aiPanel.historyPanel.renameDialog.saveButton}
                        >
                            {t("Common.save")}
                        </Button>
                    </>
                }
            >
                <TextInput
                    name="rename-conversation-input"
                    label={t("AiAgent.renameConversation.label")}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    maxLength={SESSION_NAME_MAX_LENGTH}
                    testIds={{ input: testIds.workArea.aiPanel.historyPanel.renameDialog.input }}
                    disabled={isRenaming}
                />
            </Modal>
            <AlertModal
                title={t("AiAgent.deleteConversation.failureTitle")}
                variant="Error"
                isOpen={deleteFailed}
                hideModal={() => setDeleteFailed(false)}
                buttons={
                    <Box gap="Xs">
                        <Button
                            variant="Primary"
                            testId={testIds.common.dialog.retryButton}
                            onClick={() => {
                                setDeleteFailed(false);
                                setIsDeleteDialogOpen(true);
                            }}
                        >
                            {t("Common.deleteAgain")}
                        </Button>
                        <Button
                            variant="Secondary"
                            testId={testIds.common.dialog.closeButton}
                            onClick={() => setDeleteFailed(false)}
                        >
                            {t("Common.close")}
                        </Button>
                    </Box>
                }
            >
                <Box>{t("Common.failureMessage")}</Box>
            </AlertModal>
            <AlertModal
                title={t("AiAgent.renameConversation.failureTitle")}
                variant="Error"
                isOpen={renameFailed}
                hideModal={() => setRenameFailed(false)}
                buttons={
                    <Box gap="Xs">
                        <Button
                            variant="Primary"
                            testId={testIds.common.dialog.retryButton}
                            onClick={() => {
                                setRenameFailed(false);
                                setIsRenameDialogOpen(true);
                            }}
                        >
                            {t("Common.retry")}
                        </Button>
                        <Button
                            variant="Secondary"
                            testId={testIds.common.dialog.closeButton}
                            onClick={() => setRenameFailed(false)}
                        >
                            {t("Common.close")}
                        </Button>
                    </Box>
                }
            >
                <Box>{t("Common.failureMessage")}</Box>
            </AlertModal>
        </>
    );
};

export const ConversationManagement = (props: ConversationManagementProps) => {
    const { t } = useTranslation();
    const containerRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const boxRef = React.useRef<HTMLElement[]>([]);

    const [truncatedSessionIds, setTruncatedSessionIds] = React.useState<string[]>([]);

    React.useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const updateTruncatedSessionIds = () => {
            const newlyTruncatedSessionIds = props.sessions
                .filter((_, index) => {
                    const box = boxRef.current[index];
                    return box !== undefined && isTextTruncated(box);
                })
                .map((session) => session.sessionId);

            setTruncatedSessionIds(newlyTruncatedSessionIds);
        };

        updateTruncatedSessionIds();

        const resizeObserver = new ResizeObserver(updateTruncatedSessionIds);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [props.sessions]);

    return (
        <>
            <Box
                paddingTop={props.subNavigation ? undefined : props.isMaximized ? "Lg" : "Md"}
                ref={containerRef}
                gap="Xxs"
                columnLayout
                className={style.historyPanel}
            >
                {props.isMaximized && (
                    <Box marginBottom="Xs">
                        <FeatureBadge variant="New" isSmall />
                    </Box>
                )}
                <Button
                    variant="Tertiary"
                    icon={<Add color={"--app-aiPanel-iconColor"} />}
                    disabled={props.disabled}
                    onClick={props.onNewConversation}
                    className={style.button}
                    testId={testIds.workArea.aiPanel.historyPanel.createNewButton}
                >
                    {t("AiAgent.newConversation")}
                </Button>
                {props.sessions.length > 0 && (
                    <Box verticalAlignment="Center">
                        <NavigationLabel caption>{t("AiAgent.recentChats")}</NavigationLabel>
                        <Tooltip placement="right">
                            <TooltipContent testId={testIds.workArea.aiPanel.historyPanel.tooltip}>
                                {t("AiAgent.recentConversationsHint")}
                            </TooltipContent>
                            <TooltipTrigger testId={testIds.workArea.aiPanel.historyPanel.trigger}>
                                <Info
                                    testId={testIds.workArea.aiPanel.historyPanel.infoIcon}
                                    className={style.infoIcon}
                                    size={16}
                                />
                            </TooltipTrigger>
                        </Tooltip>
                    </Box>
                )}

                <Box
                    columnLayout
                    className={classNames(style.navigationSubmenuSessionList, style.scrollContainer)}
                    ref={scrollContainerRef}
                    overflowY="Auto"
                >
                    {props.sessions.map((session, index) => (
                        <Box
                            key={index}
                            className={classNames(style.navigationSubmenuSessionRow, {
                                [style.selected]: session.sessionId === props.activeSessionId,
                            })}
                        >
                            <Box
                                style={{ flexGrow: 1, minWidth: 0 }}
                                aria-disabled={props.disabled}
                                onClick={() => !props.disabled && props.onLoadConversation(session.sessionId)}
                                data-testid={testIds.workArea.aiPanel.historyPanel.historyLink}
                                data-value={session.sessionId}
                            >
                                <Tooltip placement="top">
                                    {truncatedSessionIds.includes(session.sessionId) && (
                                        <TooltipContent>{sessionLabel(session)}</TooltipContent>
                                    )}
                                    <TooltipTrigger className={style.tooltipTrigger}>
                                        <span
                                            ref={(element) => element && (boxRef.current[index] = element)}
                                            className={style.navigationSubmenuSessionLabel}
                                        >
                                            {sessionLabel(session)}
                                        </span>
                                    </TooltipTrigger>
                                </Tooltip>
                            </Box>
                            <div className={style.kebabMenu}>
                                <SessionKebabMenu
                                    session={session}
                                    disabled={props.disabled}
                                    onSessionDeleted={props.onSessionDeleted}
                                    onSessionRenamed={props.onSessionRenamed}
                                    containerRef={containerRef}
                                    isSubNavigation={props.subNavigation}
                                />
                            </div>
                        </Box>
                    ))}
                </Box>
            </Box>
        </>
    );
};
